import React, { useContext, useState, useEffect } from "react";
import { AiFillDelete } from "react-icons/ai";
import { LuChevronLeftSquare, LuChevronRightSquare } from "react-icons/lu";
import { Lock } from "lucide-react";
import { ContextApp } from "../utils/Context.jsx";
import { ThemeContext } from "../components/ThemeContext";
import NewChatButton from "./ui/NewChatButton.tsx";
import { Spin } from "antd";
import AccessErrorAlert from "./ui/AccessErrorAlert.tsx"; 

const RightSidebar = () => {
  const {
    resetChat,
    topics = [],
    loadChatHistoryForTopic,
    deleteTopicFromList,
    isGuest,
    setIsChatOpened,
    collections,
    selectedCollection,
    setSelectedCollection,
    loadCollections,
    loadDocumentsForCollection,
    lastError,
    setLastError,
    isLoadingChat,
    hasAccessError, 
    webSocketServiceInstance,
    currentTopic,
    updateCollectionContext,
  } = useContext(ContextApp);

  const { isDarkMode } = useContext(ThemeContext);
  const { setIsSidebarCollapsed } = useContext(ContextApp);
  const [config, setConfig] = useState({ DEFAULT_COLLECTION: "public" });

  const fetchConfig = async () => {
    try {
      const response = await fetch("/configuration.json");
      const data = await response.json();
      setConfig(data);
    } catch (error) {
      console.error("Error loading config:", error);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [collectionError, setCollectionError] = useState("");
  const [isLoadingCollections, setIsLoadingCollections] = useState(false);
  const [textConfig, setTextConfig] = useState({});

  const fetchTextConfig = async () => {
    try {
      const response = await fetch("/text.json");
      const data = await response.json();
      setTextConfig(data);
    } catch (error) {
      console.error("Error loading text config:", error);
    }
  };

  useEffect(() => {
    fetchTextConfig();
  }, []);

  useEffect(() => {
    const checkScreenSize = () => {
      const isMobileOrTablet = window.innerWidth < 1024;
      setIsCollapsed(isMobileOrTablet);
      setIsSidebarCollapsed(isMobileOrTablet);
    };
    checkScreenSize();

    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, [setIsSidebarCollapsed]);
  
  useEffect(() => {
    if (collections && collections.length > 0 && !selectedCollection) {
      setSelectedCollection(collections[0]);
      localStorage.setItem("selectedCollection", collections[0]);
      loadDocumentsForCollection(collections[0]);
    }
  }, [collections]);
  
  useEffect(() => {
    const fetchCollections = async () => {
      setIsLoadingCollections(true);
      try {
        const availableCollections = await loadCollections();

        if (
          availableCollections &&
          availableCollections.length > 0 &&
          !selectedCollection
        ) {
          setSelectedCollection(availableCollections[0]);
          localStorage.setItem("selectedCollection", availableCollections[0]);
          loadDocumentsForCollection(availableCollections[0]);
        }
      } catch (error) {
      } finally {
        setIsLoadingCollections(false);
      }
    };

    if (!hasAccessError) {
      fetchCollections();
    }
  }, [hasAccessError]); 

  useEffect(() => {
    if (collections.length > 0 && !selectedCollection) {
      setSelectedCollection(collections[0]);
      localStorage.setItem("selectedCollection", collections[0]);
      loadDocumentsForCollection(collections[0]);
    }
  }, [collections]);


  useEffect(() => {
    if (lastError && !hasAccessError) {
      setCollectionError(lastError);
      setTimeout(() => {
        setCollectionError("");
        setLastError(null);
      }, 3000);
    }
  }, [lastError, setLastError, hasAccessError]);

  const handleCollapse = () => {
    setIsCollapsed(!isCollapsed);
    setIsSidebarCollapsed(!isCollapsed);
  };

const handleOpenChat = async (topicId) => {
  setIsChatOpened(true);
  
  if (collections && collections.length > 0) {
    const defaultCollection = collections[0];
    setSelectedCollection(defaultCollection);
    localStorage.setItem("selectedCollection", defaultCollection);
  }
  
  await loadChatHistoryForTopic(topicId);
};

  const handleDeleteTopic = (e, topicId) => {
    e.stopPropagation();
    deleteTopicFromList(topicId);
  };

const handleCollectionSelect = async (collection) => {
  if (collection === selectedCollection) {
    return;
  }   
  setCollectionError(""); 
  
  setSelectedCollection(collection);
  localStorage.setItem("selectedCollection", collection);
  
  if (webSocketServiceInstance) {
    await updateCollectionContext(collection, currentTopic?.id || "");
  }
  
  const hasAccess = await loadDocumentsForCollection(collection, false);
  
  if (!hasAccess && !hasAccessError) {
    const errorMessage = textConfig.RIGHT_SIDEBAR_NO_ACCESS_ERROR?.replace('{collection}', collection) || `No tienes acceso a la colección: ${collection}`;
    setCollectionError(errorMessage);
    setTimeout(() => {
      setCollectionError("");
    }, 3000);
  }

  };

  const handleNewChat = () => {    
    resetChat(); 
    setIsChatOpened(true);
  
    if (selectedCollection) {
      setTimeout(() => {
        if (webSocketServiceInstance) {
          webSocketServiceInstance.updateContext(selectedCollection, "");
        }
        
        loadDocumentsForCollection(selectedCollection);
      }, 1000);
    }
  };
  return (
    <div
      className={`fixed top-0 right-0 h-full border-l z-50 shadow-lg transition-all duration-300 flex flex-col ${
        isCollapsed ? "w-[60px]" : "w-[300px]"
      } ${
        isDarkMode
          ? "bg-neutral-800 text-white border-neutral-700"
          : "bg-gray-200 text-black"
      }`}
    >
      <div className="p-4 mt-5 flex justify-start">
        <button
          className={`${
            isDarkMode
              ? "text-gray-400 hover:text-white"
              : "text-gray-600 hover:text-black"
          } transition-all duration-300`}
          onClick={handleCollapse}
          aria-label={isCollapsed ? (textConfig.RIGHT_SIDEBAR_OPEN_PANEL || "Abrir panel") : (textConfig.RIGHT_SIDEBAR_CLOSE_PANEL || "Cerrar panel")}
        >
          {isCollapsed ? (
            <LuChevronLeftSquare
              size={38}
              className="transform transition-transform duration-300"
            />
          ) : (
            <LuChevronRightSquare
              size={38}
              className="transform transition-transform duration-300"
            />
          )}
        </button>
      </div>

      {!isCollapsed && hasAccessError && (
        <div className="mx-4 mb-2">
          <AccessErrorAlert message={lastError} isDarkMode={isDarkMode} />
        </div>
      )}

      {!isCollapsed && collectionError && !hasAccessError && (
        <div className="mx-4 mb-2 p-2 bg-red-500 text-white rounded-md">
          {collectionError}
        </div>
      )}

      {!isCollapsed && (
        <div className="flex flex-col h-full p-4">
          {!isGuest && (
            <>
                             {/* Sección de Historial - Altura fija */}
               <div className="flex-shrink-0 mb-4">
                 <h2 className="text-xl font-bold text-green-500 mb-2">{textConfig.RIGHT_SIDEBAR_HISTORY_TITLE || "Historial"}</h2>
                 <div
                   className="overflow-y-auto"
                   style={{ 
                     maxHeight: "350px",
                     minHeight: "350px",
                     scrollbarWidth: "thin",
                     scrollbarColor: isDarkMode ? "#4B5563 transparent" : "#9CA3AF transparent"
                   }}
                 >
                  <div className="flex flex-col space-y-2">
                    {isLoadingChat ? (
                      <div className="flex justify-center p-4">
                        <Spin />
                      </div>
                    ) : topics.length > 0 ? (
                      topics.map((topic) => (
                        <div
                          key={topic.id}
                          className={`flex justify-between items-center p-2 rounded-md cursor-pointer font-medium ${
                            isDarkMode
                              ? "bg-neutral-900 hover:bg-gray-700"
                              : "bg-gray-100 hover:bg-gray-200"
                          }`}
                          onClick={() => handleOpenChat(topic.id)}
                        >
                          <span className="truncate">{topic.name}</span>
                          <AiFillDelete
                            className="text-red-500 hover:text-red-700"
                            onClick={(e) => handleDeleteTopic(e, topic.id)}
                          />
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500">{textConfig.RIGHT_SIDEBAR_NO_TOPICS_AVAILABLE || "No hay temas disponibles"}</p>
                    )}
                  </div>
                </div>
              </div>

                             {/* Sección de Colecciones - Altura fija */}
               <div className="flex-shrink-0 mb-2">
                 <h2 className="text-lg font-bold mb-2">{textConfig.RIGHT_SIDEBAR_COLLECTIONS_TITLE || "Colecciones"}</h2>
                 <div
                   className="overflow-y-auto"
                   style={{ 
                     maxHeight: "150px",
                     minHeight: "150px",
                     scrollbarWidth: "thin",
                     scrollbarColor: isDarkMode ? "#4B5563 transparent" : "#9CA3AF transparent"
                   }}
                 >
                  <div className="flex flex-col space-y-2">
                    {isLoadingCollections ? (
                      <div className="flex justify-center p-4">
                        <Spin />
                      </div>
                    ) : collections.length > 0 ? (
                      collections.map((collection) => (
                        <div
                          key={collection}
                          className={`p-2 rounded-md cursor-pointer font-medium flex items-center justify-between ${
                            selectedCollection === collection
                              ? isDarkMode
                                ? "bg-gray-700"
                                : "bg-gray-300"
                              : isDarkMode
                              ? "bg-neutral-900 hover:bg-gray-700"
                              : "bg-gray-100 hover:bg-gray-200"
                          }`}
                          onClick={() => handleCollectionSelect(collection)}
                        >
                          <span>{collection}</span>
                          {collection === config.DEFAULT_COLLECTION && (
                            <Lock 
                              size={14} 
                              className={isDarkMode ? "text-gray-400" : "text-gray-600"} 
                            />
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500">
                        {textConfig.RIGHT_SIDEBAR_NO_COLLECTIONS_AVAILABLE || "No hay colecciones disponibles"}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {!isCollapsed && (
        <div className="flex-shrink-0 p-4">
          <NewChatButton onClick={handleNewChat} />
        </div>
      )}

{isCollapsed && collections && collections.length > 0 && (
  <div className="p-2 mt-4 flex flex-col gap-2">
    {collections.slice(0, 3).map((collection) => (
      <div
        key={collection}
        className={`p-2 rounded-md text-center cursor-pointer relative ${
          selectedCollection === collection
            ? isDarkMode
              ? "bg-gray-700 text-white"
              : "bg-gray-300 text-black"
            : isDarkMode
              ? "bg-neutral-900 text-gray-300 hover:bg-gray-700"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
        }`}
        style={{
          writingMode: "vertical-rl",
          textOrientation: "mixed",
          height: "110px",
          fontSize: "0.85rem"
        }}
        onClick={() => handleCollectionSelect(collection)}
      >
        {collection}
        {collection === config.DEFAULT_COLLECTION && (
          <Lock 
            size={12} 
            className={`absolute top-1 right-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            style={{ transform: "rotate(90deg)" }}
          />
        )}
      </div>
    ))}
  </div>
)}
         </div>
   );
 };

export default RightSidebar;